import { type RefObject, useState } from "react"

import { useTranslation } from "react-i18next"
import { Button, Divider, Modal } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"

import Questions from "components/challenges/Challenge/Questions/Questions"
import ReportContentChip from "components/lib/ReportContent/ReportContentChip"
import { SideNav } from "components/lib/SideNav"
import { PlaceholderItem, PlaceholderStack } from "components/lib/UI"
import { useAppSelector } from "store"
import useTheme from "theme/useTheme"
import util from "utils/utils"

import IdeaTags from "../Tags"
import IdeaTeam, { RequestIdeaTeam } from "../Team"
import useScrollTracking, { type ItemRefs } from "../useIdeaScrollTracking"
import { createAssessmentSectionEntry } from "./AssessmentSectionEntry"
import { createCustomSectionEntry } from "./CustomSectionEntry"
import { createImpactSectionEntry } from "./ImpactSectionEntry"

type IdeaSideBarProps = {
  idea: OpenAPI.GET<"/ideas/{id}">["response"] & { ownerIdeaTemplate?: OpenAPI.Schemas["IdeaTemplate"] }
  assessment?: OpenAPI.GET<"/ideas/{id}">["response"]["assessments"][0]
  loading: boolean
  isEditing: boolean
  sectionRefs: RefObject<ItemRefs>
  fieldRefs: RefObject<ItemRefs>
  scrollRef?: RefObject<HTMLDivElement>
  updateIdea: (idea: any) => void
  leaveIdea: () => void
  main: any
}

const IdeaSidebar = ({
  idea,
  assessment,
  loading,
  isEditing,
  sectionRefs,
  fieldRefs,
  updateIdea,
  leaveIdea,
  main,
  scrollRef,
}: IdeaSideBarProps) => {
  const { t } = useTranslation()
  const [isShowingQuestions, setIsShowingQuestions] = useState(false)
  const user = useAppSelector((state) => state.user)

  const theme = useTheme()
  const { activeItemId, scrollToItem } = useScrollTracking(sectionRefs, scrollRef, -260)
  const fieldScroll = useScrollTracking(fieldRefs, scrollRef, -300)

  if (!idea) {
    return null
  }

  const { ownerIdeaTemplate } = idea
  const { body = [] } = ownerIdeaTemplate ?? {}

  const canEdit = util.canEditIdea(user, idea)
  const canManageChallenge = util.canManageChallenge(user, idea.ownerChallenge)

  return (
    <>
      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={isShowingQuestions}
        onClose={() => setIsShowingQuestions(false)}
      >
        <Modal.Content scrolling>
          <Questions challenge={idea.ownerChallenge} nonAdminView />
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setIsShowingQuestions(false)}>Close</Button>
        </Modal.Actions>
      </Modal>
      <SideNav
        fluid={!main}
        top={isEditing ? 140 : 100}
        beforeContent={
          theme.sizes.isComputer || !main ? (
            <h5 style={{ color: "#333", marginBottom: 10 }}>
              {t("common:capitalise", { key: "generic.idea" })} sections
            </h5>
          ) : null
        }
        navItems={
          !theme.sizes.isComputer && !!main
            ? undefined
            : [
                loading
                  ? {
                      key: "loading",
                      type: "custom" as const,
                      content: (
                        <PlaceholderStack gap={15}>
                          <PlaceholderItem height={40} />
                          <PlaceholderItem height={40} />
                          <PlaceholderItem height={40} />
                        </PlaceholderStack>
                      ),
                    }
                  : null,
                ...body.flatMap((section) => {
                  if (section.type === "impact") {
                    return createImpactSectionEntry({
                      user,
                      idea,
                      section,
                      activeItemId,
                      scrollToItem,
                      isEditing,
                    })
                  }
                  if (util.shouldShowIdeaSection(idea, section)) {
                    if (section.type === "assessment") {
                      return createAssessmentSectionEntry({
                        user,
                        idea,
                        assessment,
                        section,
                        activeSectionId: activeItemId,
                        scrollToSection: scrollToItem,
                        activeFieldId: fieldScroll.activeItemId,
                        scrollToField: fieldScroll.scrollToItem,
                        isEditing,
                      })
                    }
                    return createCustomSectionEntry({
                      user,
                      idea,
                      section,
                      activeItemId,
                      scrollToItem,
                      isEditing,
                    })
                  }
                  return null
                }),
                (isEditing || idea.attachments?.length > 0) &&
                !idea?.ownerIdeaTemplate?.disableAttachments &&
                !theme.sizes.isMobile
                  ? {
                      key: "attachments",
                      type: "link" as const,
                      content: t("generic.attachments"),
                      iconLeft: "paperclip",
                      active: activeItemId === "attachments",
                      onClick: () => scrollToItem("attachments"),
                    }
                  : null,
                (!isEditing || idea.commentCount >= 1) &&
                !idea.ownerChallenge?.preventIdeaComments &&
                !theme.sizes.isMobile &&
                !loading
                  ? {
                      key: "comments",
                      type: "link" as const,
                      content: t("generic.comments"),
                      iconLeft: "discussions",
                      active: activeItemId === "comments",
                      onClick: () => scrollToItem("comments"),
                    }
                  : null,

                !loading && idea.ownerChallenge?.questionsEnabled
                  ? {
                      key: "questions",
                      type: "link" as const,
                      content: t("generic.questions"),
                      iconLeft: "talk",
                      active: activeItemId === "questions",
                      onClick: () => {
                        setIsShowingQuestions(!isShowingQuestions)
                      },
                    }
                  : null,
              ].filter((item) => Boolean(item))
        }
        nav={
          !theme.sizes.isComputer &&
          isEditing && (
            <div style={{ padding: "0px", marginBottom: 10 }}>
              <IdeaTags updateIdea={updateIdea} idea={idea} isSidebar />
              {user && !idea?.ownerChallenge?.ideaAnonymity && (isEditing || canManageChallenge) && (
                <IdeaTeam updateIdea={updateIdea} idea={idea} isSidebar canEdit={canEdit} leaveIdea={leaveIdea} />
              )}
            </div>
          )
        }
        main={main}
        afterContent={
          !main ? null : (
            <>
              {user && !idea?.ownerChallenge?.ideaAnonymity && (isEditing || canManageChallenge) ? (
                <>
                  <Divider />
                  <div style={{ padding: "0 12px" }}>
                    <IdeaTeam updateIdea={updateIdea} idea={idea} isSidebar canEdit={canEdit} leaveIdea={leaveIdea} />
                  </div>
                </>
              ) : null}
              {user && !util.canEditIdea(user, idea) && !idea?.ownerChallenge?.preventCollaboration && !loading ? (
                <>
                  <Divider fitted hidden={!idea?.ownerChallenge?.ideaAnonymity && (isEditing || canManageChallenge)} />
                  <RequestIdeaTeam idea={idea} updateIdea={updateIdea} />
                </>
              ) : null}
              {user &&
              isEditing &&
              idea?.ownerChallenge?.tagList.filter((tag) => !tag?.disabledEntities?.includes("ideas")).length > 0 ? (
                <>
                  <Divider />
                  <div style={{ padding: "0 12px" }}>
                    <h5 style={{ color: "#333", marginBottom: 10 }}>{t("ideas.tags.title")}</h5>
                    <IdeaTags updateIdea={updateIdea} idea={idea} isSidebar />
                  </div>
                </>
              ) : null}
              {!(isEditing && canEdit) ? (
                <>
                  <Divider />
                  <ReportContentChip forType="idea" forId={idea._id} />
                </>
              ) : null}
            </>
          )
        }
      />
    </>
  )
}

export default IdeaSidebar
